<template>
  <div class="p-10 flex flex-col flex-grow">
    <div class="w-full rounded-lg shadow-sm bg-white p-3 flex flex-col">
      <h1 class="text-md">{{ translate("Kiosks") }}</h1>
      <div class="w-full flex items-center gap-2 my-4">
        <BaseButton
          :label="translate('Create Kiosk')"
          variant="primary"
          @click="handleAddDevice"
          id="kiosks.button.create"
        />
        <BaseButton
          :label="translate('Sync All Devices')"
          variant="primary"
          @click="syncDevices"
          id="kiosks.button.sync"
        />

        <MaintenanceActionButton @fetchDevices="getKioskDevices" />

        <div class="relative ml-auto flex items-cente gap-2">
          <div class="relative">
            <input
              type="text"
              :placeholder="translate('Search Kiosk')"
              class="w-full px-3 py-2 placeholder-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              v-model="search.text"
              @keyup.enter="filterKiosk"
              id="kiosks.input.search"
            />
            <span
              v-if="search.status"
              class="absolute right-2 cursor-pointer leading-10"
              @click="resetSearch"
              id="kiosks.button.resetsearch"
              >x</span
            >
          </div>

          <BaseButton
            :label="translate('SEARCH')"
            variant="primary"
            @click="filterKiosk"
            :loading="search.loading"
            id="kiosks.button.search"
          />
        </div>
      </div>
      <KiosksItemsTable
        id="kiosks.table.lists"
        :headers="tableHeaders"
        :rows="devices"
        :perPage="table.perPage"
        :totalPages="table.totalPages"
        @page-changed="onPageChange"
        @edit="handleEditDevice"
        @view="handleViewDevice"
        @refresh-mdm="handleRefreshMdm"
        @reset-alarm="handleResetAlarm"
        @deactivate-kiosk="handleDeactivateKiosk"
        ref="kioskItemTable"
        :loading="table.loading"
      />
    </div>

    <ConfirmationModal id="kiosks.modal.confirm" ref="confirmModal" />

    <KioskFormModal
      id="kiosks.modal.form"
      :initialValue="modal.form.data"
      @onSucess="handleKioskFormSuccess"
      @onError="handleKioskFormError"
      v-model="modal.form.status"
      @deactivate-kiosk="handleDeactivateKiosk"
    />
  </div>
</template>
<script>
import {
  Device as DeviceService,
  Location as LocationService,
} from "@/services/SOLO";
import { translations, notificationAlerts, eventHandler } from "@/mixins";
import KiosksItemsTable from "./components/V2/KiosksItemsTable.vue";
import _ from "lodash";
import ConfirmationModal from "@/components/V2/Modals/ConfirmationModal.vue";
import BaseButton from "@/components/V2/BaseButton.vue";
import KioskFormModal from "./components/V2/KioskFormModal.vue";
import MaintenanceActionButton from "./components/MaintenanceActionButton.vue";

export default {
  mixins: [translations, notificationAlerts],
  components: {
    KiosksItemsTable,
    ConfirmationModal,
    BaseButton,
    KioskFormModal,
    MaintenanceActionButton,
  },
  data() {
    return {
      search: {
        text: "",
        loading: false,
        status: false,
      },
      table: {
        lists: [],
        page: 1,
        perPage: 10,
        loading: false,
        headers: [
          { label: "Alarm", key: "status" },
          { label: "Device ID", key: "id" },
          { label: "Name", key: "label" },
          { label: "Status", key: "available" },
          { label: "Location", key: "location" },
          { label: "Payment Device", key: "terminal" },
          { label: "Last Seen", key: "lastseen" },
          { label: "Alarm Information", key: "alarm" },
          { label: "Action", key: "action" },
        ],
        totalPages: 1,
        loading: false,
      },
      debouncedPageChange: null,
      modal: {
        form: {
          status: false,
          data: null,
        },
      },
    };
  },
  computed: {
    devices() {
      return this.table.lists.map((device) => {
        const formatted = {
          id: device.id,
          status: device.attributes.status,
          label: device.attributes.label,
          available: device.attributes.available,
          location: device.attributes["location-name"],
          terminal: device.attributes["terminal-id"],
          ipaddress: device.attributes["ip-address"],
          lastseen: device.attributes["last-seen-at-utc"],
          alarm: device.attributes.alarm,
          created_at: device.attributes["created-at"],
          is_enrolled: device.attributes["is-enrolled"],
        };

        return { ...formatted, ...device };
      });
    },
    tableHeaders() {
      return this.table.headers.map((header) => {
        return {
          ...header,
          label: this.translate(header.label),
        };
      });
    },
  },
  watch: {
    "table.page": function () {
      this.debouncedPageChange();
    },
  },
  methods: {
    async getKioskDevices() {
      this.table.loading = true;
      await DeviceService.all(this.table.page, this.table.perPage).then(
        (response) => {
          const { data, meta } = response.data;
          this.setDevices(data, meta);
        }
      );
      this.table.loading = false;
    },
    setDevices(data, meta) {
      this.table.lists = data;
      this.table.totalPages = meta.pagination.total_pages;
    },
    onPageChange(page) {
      this.table.page = page;
    },
    handleAddDevice() {
      this.modal.form.data = null;
      this.modal.form.status = true;
    },
    handleEditDevice(device) {
      this.modal.form.data = device;
      this.modal.form.status = true;
    },
    handleViewDevice(device) {
      this.$router.push({ name: "Kiosk_details", params: { id: device.id } });
    },
    handleRefreshMdm(device) {
      DeviceService.getMdmDeviceDetails(device.id)
        .then(() => {
          this.getKioskDevices();
          this.successNotification(
            "DEVICE DETAILS LOADED",
            "Device details has been loaded!"
          );
        })
        .catch(() => {
          this.systemErrorNotification(
            "NOTIFICATION",
            "Failed to refresh device details."
          );
        });
    },
    async handleResetAlarm(device) {
      const payload = { "reset-alarm": true, _method: "PATCH" };
      await DeviceService.resetAlarm(device.id, payload)
        .then(() => {
          this.getKioskDevices();
          this.successNotification(
            "DEVICE DETAILS",
            "Alarm successfully reset!"
          );
        })
        .catch(() => {
          this.systemErrorNotification(
            "NOTIFICATION",
            "Failed to reset alarm."
          );
        })
        .finally(() => {
          this.$refs.kioskItemTable.closeDetailsPopup();
        });
    },
    async handleDeactivateKiosk(device) {
      const statusValue = device.available ? 0 : 1;

      if (statusValue == 0) {
        return false;
      }

      const message = this.translate(
        "Deactivating the device will stop it immediately! Are you sure you want to deactivate this device?"
      );

      const confirmed = await this.$refs.confirmModal.open({
        title: this.translate("Confirmation"),
        message: this.translate(message),
      });

      if (confirmed) {
        DeviceService.update(device.id, { available: statusValue })
          .then((response) => {
            this.getKioskDevices();
            this.successNotification(
              "NOTIFICATION",
              "Device successfully deactivated!"
            );

            device.available = statusValue;
            this.modal.form.data = device;
          })
          .catch((err) => {
            this.systemErrorNotification(
              "NOTIFICATION",
              "Failed to deactivate device."
            );
          });
      }
    },
    async syncDevices() {
      const confirmed = await this.$refs.confirmModal.open({
        title: this.translate("Confirmation"),
        message: this.translate("Do you want to proceed this change?"),
      });

      if (confirmed) {
        await DeviceService.syncAllDevices()
          .then((response) => {
            this.successNotification(
              this.translate("NOTIFICATION"),
              this.translate("Device successfully synced!")
            );
          })
          .catch((error) => {
            this.systemErrorNotification(
              "NOTIFICATION",
              "Failed to sync devices."
            );
          });
      }
    },
    async filterKiosk() {
      this.search.loading = true;
      DeviceService.search(this.search.text)
        .then((response) => {
          const { data, meta } = response.data;
          this.setDevices(data, meta);
          this.search.status = true;
        })
        .finally(() => {
          this.search.loading = false;
        });
    },
    resetSearch() {
      this.getKioskDevices();
      this.search.status = false;
      this.search.text = "";
    },
    handleKioskFormSuccess() {
      this.getKioskDevices();
      this.successNotification(
        this.translate("NOTIFICATION"),
        this.translate("Device has been saved successfully.")
      );
      this.modal.form.status = false;
    },
    async handleKioskFormError(error) {
      const messages = Object.entries(error.response.data.messages)
        .map(([key, value]) => `${key}: ${value[0]}`)
        .join("\n");

      this.systemErrorNotification(
        "NOTIFICATION",
        `${error.response.data.error[0].detail}\n${messages}`
      );
    },
  },
  mounted() {
    this.getKioskDevices();
  },
  created() {
    this.debouncedPageChange = _.debounce(this.getKioskDevices, 500);
  },
};
</script>
